/**
 * Creates a safe window opener for use with async operations.
 * Safari blocks window.open() inside async callbacks (treats them as popups).
 *
 * Usage:
 *   const opener = createSafeWindowOpener()
 *   const res = await someApiCall()
 *   opener.open(res.url)
 *
 * Call this synchronously in the click handler BEFORE any await.
 * Then call .open(url) after the async work resolves.
 * Call .close() if the async operation fails and you don't want a blank tab.
 */
export const createSafeWindowOpener = () => {
  const newWindow = window.open('', '_blank')

  // Write loading content to prevent iOS Safari from blocking/closing
  // the blank tab. Mobile Safari aggressively closes empty popup windows.
  if (newWindow) {
    try {
      newWindow.document.write(
        '<html><head><title>Loading...</title></head>' +
          '<body style="display:flex;align-items:center;justify-content:center;' +
          'height:100vh;margin:0;font-family:-apple-system,system-ui,sans-serif;' +
          'background:#f5f5f5;color:#666">' +
          '<p>Loading...</p></body></html>',
      )
      newWindow.document.close()
    } catch {
      // Ignore cross-origin write errors
    }
  }

  return {
    open: (url: string) => {
      if (newWindow && !newWindow.closed) {
        newWindow.location.href = url
      } else {
        // Fallback: create an anchor element and click it programmatically.
        // This works even when window.open was blocked by popup blocker.
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    close: () => {
      if (newWindow && !newWindow.closed) {
        newWindow.close()
      }
    },
  }
}
